import React, { useEffect, useMemo, useState } from 'react';
import styles from './saved-signature-slider.module.css';
import { FormattedMessage } from 'react-intl';
import { Box } from '@dropbox/dig-foundations';
import { IconButton } from '@dropbox/dig-components/buttons';
import { UIIcon } from '@dropbox/dig-icons';
import {
  ChevronLeftLine,
  ChevronRightLine,
  DeleteLine,
} from '@dropbox/dig-icons/assets';
import { Text } from '@dropbox/dig-components/typography';
import { Chip } from '@dropbox/dig-components/chip';
import { SigData } from '../../context/signer-app-client/signature';
import ChipButtonsWrapper from '../common/chip-buttons-wrapper';
import { SignatureModalContext } from '../signature-modal-context/context';
import SavedSignature from './saved-signature-v2';

type SavedSignatureSliderProps = {
  signatures: SigData[];
  getSignatureUrl: (signature: SigData) => string;
  onSelectSignature: (signature: SigData) => void;
  onDelete: SignatureModalContext['onSavedSignatureRemove'];
};

export default function SavedSignatureSlider({
  signatures,
  getSignatureUrl,
  onSelectSignature,
  onDelete,
}: SavedSignatureSliderProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const activeSignature = useMemo(() => {
    if (!signatures.length) {
      return null;
    }

    if (!signatures[activeIndex]) {
      setActiveIndex(0);
      return null;
    }

    return signatures[activeIndex];
  }, [signatures, activeIndex]);

  useEffect(() => {
    if (activeSignature) {
      onSelectSignature(activeSignature);
    }
  }, [activeSignature, onSelectSignature]);

  const onUpdateActiveIndex = (value = 1) => {
    if (signatures.length === activeIndex + value) {
      setActiveIndex(0);
      return;
    }

    if (activeIndex + value < 0) {
      setActiveIndex(signatures.length - 1);
      return;
    }

    setActiveIndex((prev) => prev + value);
  };

  if (!activeSignature) {
    return null;
  }

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      height="100%"
      borderColor="Border Subtle - On Base"
      borderWidth="1"
      borderStyle="Solid"
      position="relative"
      paddingX="Micro Medium"
      data-testid="saved-signature-slider"
    >
      <Text size="xsmall" color="subtle" className={styles.infoText}>
        <FormattedMessage
          id="814f149c8571d763926de404038982aba56b8e415c8e8267cbbeeecdc283c64d"
          description="Shows info about currenlty active signature number and total signatures number"
          defaultMessage="{active}/{total} saved signatures"
          values={{
            active: activeIndex + 1,
            total: signatures.length,
          }}
        />
      </Text>

      <IconButton
        variant="transparent"
        size="large"
        onClick={() => onUpdateActiveIndex(-1)}
        disabled={signatures.length === 1}
        aria-label="chevron-left-line"
      >
        <UIIcon src={ChevronLeftLine} />
      </IconButton>

      <Box className={styles.signatureContainer}>
        <SavedSignature
          index={activeIndex}
          signatureSrc={getSignatureUrl(activeSignature)}
        />
      </Box>

      <IconButton
        variant="transparent"
        size="large"
        onClick={() => onUpdateActiveIndex(1)}
        disabled={signatures.length === 1}
        aria-label="chevron-right-line"
        data-testid="saved-signature-next-btn"
      >
        <UIIcon src={ChevronRightLine} />
      </IconButton>

      <ChipButtonsWrapper>
        <Chip
          data-testid="signing-modal--delete"
          onClick={() => onDelete(activeSignature)}
          size="small"
        >
          <Chip.IconAccessory>
            <UIIcon src={DeleteLine} />
          </Chip.IconAccessory>
          <Chip.Content>
            <FormattedMessage
              id="51dc6bc35e8ae4c2d22f0ee6b3883a9b889702c40f7434b73cdc2f03e6307578"
              description="Text for a button which deletes the signature when clicked."
              defaultMessage="Clear"
            />
          </Chip.Content>
        </Chip>
      </ChipButtonsWrapper>
    </Box>
  );
}
